(function () {
  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function parseCoordinate(value) {
    const parsed = parseFloat(String(value || '').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  }

  function buildPopup(retailer, linkLabel) {
    const address = retailer.address ? `<span>${escapeHtml(retailer.address)}</span>` : '';
    const description = retailer.description ? `<p>${escapeHtml(retailer.description)}</p>` : '';
    const link = retailer.link
      ? `<a href="${escapeHtml(retailer.link)}">${escapeHtml(linkLabel || 'Mehr erfahren')}</a>`
      : '';

    return `
      <strong>${escapeHtml(retailer.name)}</strong>
      ${address}
      ${description}
      ${link}
    `;
  }

  function buildStorePopup(store, linkLabel) {
    const image = store.image
      ? `<img src="${escapeHtml(store.image)}" alt="${escapeHtml(store.name)}" loading="lazy">`
      : '';
    const label = store.label ? `<span>${escapeHtml(store.label)}</span>` : '';
    const address = store.address ? `<p>${escapeHtml(store.address)}</p>` : '';
    const hours = store.hours ? `<p>${escapeHtml(store.hours)}</p>` : '';
    const phone = store.phone ? `<p>${escapeHtml(store.phone)}</p>` : '';
    const link = store.link
      ? `<a href="${escapeHtml(store.link)}" target="_blank" rel="noopener noreferrer">${escapeHtml(linkLabel || 'Route planen')}</a>`
      : '';

    return `
      <div class="wig-store-locator-popup">
        ${image}
        ${label}
        <strong>${escapeHtml(store.name)}</strong>
        ${address}
        ${hours}
        ${phone}
        ${link}
      </div>
    `;
  }

  function createStoreMarkerIcon(isActive, isFeatured) {
    const size = isFeatured ? 42 : 36;
    const classes = [
      'wig-store-locator-marker',
      isFeatured ? 'is-featured' : '',
      isActive ? 'is-active' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return L.divIcon({
      className: classes,
      html: `
        <span class="wig-store-locator-marker__cheese" aria-hidden="true">
          <span class="wig-store-locator-marker__hole wig-store-locator-marker__hole--one"></span>
          <span class="wig-store-locator-marker__hole wig-store-locator-marker__hole--two"></span>
          <span class="wig-store-locator-marker__hole wig-store-locator-marker__hole--three"></span>
        </span>
      `,
      iconSize: [size, size],
      iconAnchor: [size / 2, size],
      popupAnchor: [0, -size + 4],
    });
  }

  function loadLeaflet(callback) {
    if (window.L) {
      callback();
      return;
    }

    const existing = document.querySelector(
      'script[data-partner-locations-leaflet], script[data-farmer-map-leaflet], script[src*="leaflet"]'
    );

    if (existing) {
      existing.addEventListener('load', callback, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.defer = true;
    script.dataset.partnerLocationsLeaflet = 'true';
    script.addEventListener('load', callback, { once: true });
    document.head.appendChild(script);
  }

  function initSection(section) {
    if (section.dataset.partnerLocationsInitialized === 'true') return;

    const mapElement = section.querySelector('[data-partner-locations-map]');
    const dataElement = section.querySelector('[data-partner-locations-data]');

    if (!mapElement || !dataElement || !window.L) return;

    let config;
    try {
      config = JSON.parse(dataElement.textContent || '{}');
    } catch (error) {
      return;
    }

    const retailers = (config.retailers || [])
      .map((retailer) => ({
        ...retailer,
        lat: parseCoordinate(retailer.lat),
        lng: parseCoordinate(retailer.lng),
      }))
      .filter((retailer) => retailer.lat !== null && retailer.lng !== null);

    if (!retailers.length) return;

    section.dataset.partnerLocationsInitialized = 'true';

    const centerLat = parseCoordinate(config.centerLat);
    const centerLng = parseCoordinate(config.centerLng);
    const center = [
      centerLat !== null ? centerLat : retailers[0].lat,
      centerLng !== null ? centerLng : retailers[0].lng,
    ];

    const map = L.map(mapElement, {
      scrollWheelZoom: Boolean(config.scrollWheelZoom),
    }).setView(center, config.zoom || 10);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
    }).addTo(map);

    const bounds = [];
    retailers.forEach((retailer) => {
      const marker = L.marker([retailer.lat, retailer.lng]).addTo(map);
      marker.bindTooltip(retailer.name || '', { direction: 'top' });
      marker.bindPopup(buildPopup(retailer, config.popupLinkLabel));
      bounds.push([retailer.lat, retailer.lng]);
    });

    if (config.fitMarkers && bounds.length > 1) {
      map.fitBounds(bounds, { padding: [32, 32], maxZoom: config.zoom || 10 });
    }

    window.setTimeout(() => map.invalidateSize(), 250);
  }

  function initStoreDealerRegistration(section) {
    if (section.dataset.storeDealerInitialized === 'true') return;

    const openButton = section.querySelector('[data-dealer-registration-open]');
    const overlay = section.querySelector('[data-dealer-registration-overlay]');
    const dialog = section.querySelector('[data-dealer-registration-dialog]');

    if (!openButton || !overlay || !dialog) return;

    section.dataset.storeDealerInitialized = 'true';

    const closeButtons = overlay.querySelectorAll('[data-dealer-registration-close]');
    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    let previousFocus = null;

    function getFocusableElements() {
      return Array.from(dialog.querySelectorAll(focusableSelector)).filter((element) => {
        return element.offsetParent !== null || element === document.activeElement;
      });
    }

    function focusDialog() {
      const status = dialog.querySelector('[data-dealer-registration-status]');
      const firstField = dialog.querySelector('input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])');
      const focusable = getFocusableElements();
      const target = status || firstField || focusable[0] || dialog;
      target.focus({ preventScroll: true });
    }

    function setOpen(isOpen) {
      if (isOpen) {
        previousFocus = document.activeElement;
        overlay.hidden = false;
        overlay.setAttribute('aria-hidden', 'false');
        openButton.setAttribute('aria-expanded', 'true');
        section.classList.add('is-dealer-registration-open');
        window.setTimeout(focusDialog, 0);
        return;
      }

      overlay.hidden = true;
      overlay.setAttribute('aria-hidden', 'true');
      openButton.setAttribute('aria-expanded', 'false');
      section.classList.remove('is-dealer-registration-open');

      if (previousFocus && typeof previousFocus.focus === 'function') {
        previousFocus.focus({ preventScroll: true });
      }
    }

    function onKeydown(event) {
      if (overlay.hidden) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = getFocusableElements();
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    openButton.addEventListener('click', () => setOpen(true));
    closeButtons.forEach((button) => button.addEventListener('click', () => setOpen(false)));
    document.addEventListener('keydown', onKeydown);

    if (dialog.querySelector('[data-dealer-registration-status]')) {
      setOpen(true);
    }
  }

  function initStoreLocator(section) {
    if (section.dataset.storeLocatorInitialized === 'true') return;

    const mapElement = section.querySelector('[data-store-locator-map]');
    const dataElement = section.querySelector('[data-store-locator-data]');

    if (!mapElement || !dataElement || !window.L) return;

    let config;
    try {
      config = JSON.parse(dataElement.textContent || '{}');
    } catch (error) {
      return;
    }

    const stores = (config.stores || [])
      .map((store) => ({
        ...store,
        lat: parseCoordinate(store.lat),
        lng: parseCoordinate(store.lng),
      }))
      .filter((store) => store.lat !== null && store.lng !== null);

    if (!stores.length) return;

    section.dataset.storeLocatorInitialized = 'true';

    const centerLat = parseCoordinate(config.centerLat);
    const centerLng = parseCoordinate(config.centerLng);
    const center = [
      centerLat !== null ? centerLat : stores[0].lat,
      centerLng !== null ? centerLng : stores[0].lng,
    ];
    const map = L.map(mapElement, {
      scrollWheelZoom: Boolean(config.scrollWheelZoom),
    }).setView(center, config.zoom || 11);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
    }).addTo(map);

    const cards = Array.from(section.querySelectorAll('[data-store-card]'));
    const markers = new Map();
    const bounds = [];

    function setActiveStore(storeId, options = {}) {
      const markerEntry = markers.get(storeId);

      cards.forEach((card) => {
        const isActive = card.dataset.storeId === storeId;
        const button = card.querySelector('[data-store-select]');
        card.classList.toggle('is-active', isActive);
        if (button) button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        if (isActive && options.scrollCard) card.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });

      markers.forEach((entry, id) => {
        entry.marker.setIcon(createStoreMarkerIcon(id === storeId, entry.store.highlight));
      });

      if (!markerEntry) return;

      if (options.pan !== false) {
        const targetZoom = Math.max(map.getZoom(), config.zoom || 11);
        map.setView([markerEntry.store.lat, markerEntry.store.lng], targetZoom, { animate: true });
      }

      if (options.openPopup !== false) {
        markerEntry.marker.openPopup();
      }
    }

    stores.forEach((store, index) => {
      const marker = L.marker([store.lat, store.lng], {
        icon: createStoreMarkerIcon(false, store.highlight),
      }).addTo(map);

      marker.bindPopup(buildStorePopup(store, config.popupLinkLabel));
      marker.on('click', () => setActiveStore(store.id, { scrollCard: true }));

      markers.set(store.id, { marker, store, index });
      bounds.push([store.lat, store.lng]);
    });

    cards.forEach((card) => {
      const button = card.querySelector('[data-store-select]');
      if (!button) return;

      button.setAttribute('aria-pressed', 'false');
      button.addEventListener('click', () => setActiveStore(card.dataset.storeId));
    });

    if (config.fitMarkers && bounds.length > 1) {
      map.fitBounds(bounds, { padding: [28, 28], maxZoom: config.zoom || 11 });
    }

    const highlightedStore = stores.find((store) => store.highlight) || stores[0];
    if (highlightedStore) {
      setActiveStore(highlightedStore.id, { openPopup: false, pan: false });
    }

    window.setTimeout(() => map.invalidateSize(), 250);
  }

  function initAll() {
    document.querySelectorAll('[data-store-locator-section]').forEach(initStoreDealerRegistration);

    loadLeaflet(() => {
      document.querySelectorAll('[data-partner-locations-section]').forEach(initSection);
      document.querySelectorAll('[data-store-locator-section]').forEach(initStoreLocator);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const partnerSection =
      event.target.matches && event.target.matches('[data-partner-locations-section]')
        ? event.target
        : event.target.querySelector('[data-partner-locations-section]');
    const storeSection =
      event.target.matches && event.target.matches('[data-store-locator-section]')
        ? event.target
        : event.target.querySelector('[data-store-locator-section]');

    if (!partnerSection && !storeSection) return;

    loadLeaflet(() => {
      if (partnerSection) initSection(partnerSection);
      if (storeSection) initStoreLocator(storeSection);
    });

    if (storeSection) initStoreDealerRegistration(storeSection);
  });
})();
